import axios from 'axios';

import { handleApiSucess, handleApiError, getRequestHeaders } from 'api/util.api';
import { handleRequest } from './util.api';

export const getFormConfigUrl = (cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/form_config`;

export const getDisableFormConfigUrl = (cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/disable_form_config`;

export const getAddUrl = (cId, sId) => `/companies/${cId}/subcompanies/${sId}/assets`;

export const getAssetsUrl = (cId, sId) => `/companies/${cId}/subcompanies/${sId}/assets`;

export const getByIdAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}`;

/**
 * @author Jorge Casas
 *
 * @function getCodesLabelsByAssetPDFUrl
 *
 * @description This function endpoint is used to get the codes labels by asset in PDF format.
 *
 * @param {string} cId - The id of the company.
 * @param {string} sId - The id of the subcompany.
 * @param {string} aId - The id of the asset.
 */
export const getCodesLabelsByAssetPDFUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/codes_labels_pdf`;

export const updateAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}`;

export const sendTokenAsignAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/sendToken`;

export const requestUpdateAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/request_update`;

export const disableAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/disable`;

export const enableAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/enable`;

export const getHistoricsUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/historics`;

export const acceptHistoricUrl = (hId, aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/historics/${hId}/accept`;

export const rejectHistoricUrl = (hId, aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/historics/${hId}/reject`;

export const outputAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/output`;

export const inputAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/input`;

export const aceptOutputAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/acept_output`;

export const rejectOutputAssetUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/reject_output`;

export const editBillUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/putBill`;

export const custodyVouchersUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/custody_vouchers`;

// Signed voucher of an employee (Reportes > Vales de resguardo), all their assets.
export const employeeCustodyVouchersUrl = (cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/custody_vouchers`;

/**
 * @author Jorge Casas
 *
 * @creationDate 23/07/2024
 *
 * @description This function endpoint is used to get the QR code of an asset.
 *
 * @param {string} aId - The id of the asset.
 *
 * @returns {string}
 *
 * @version 3.4.14
 */
export const getQRCodeUrl = (aId) => `/qr_redirect/assetId/${aId}`;

/**
 * @author Jorge Casas
 *
 * @creationDate 23/07/2024
 *
 * @description This function endpoint is used to regenerate the codes of an asset. (qrs and barcode codes of the asset)
 */
export const regenerateCodesUrl = (cId, sId, aId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/regenerate_codes`;

/**
 * @author Jorge Casas
 *
 * @creationDate 24/04/2024
 *
 * @description This function endpoint is used to edit customs documents of an asset.
 *
 * @param {string} aId - The id of the asset.
 * @param {string} cId - The id of the company.
 * @param {string} sId - The id of the subcompany.
 *
 */
export const editCustomsDocumentsUrl = (aId, cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/assets/${aId}/put_customs_documents`;

/**
 * @author Jorge Casas
 * 
 * @creationDate 25/07/2024
 * 
 * @description This function endpoint is used to download a file from a URL and convert it to base64 format.
 * 
 * @function downloadFileToConverBase64Url
 * 
 * @returns {string}
 * 
 * @version 3.4.16
 * 
 */
export const downloadFileToConverBase64Url = () => `/download-file-and-convert-to-base64`;

const service = {
  configure(endpoint = '', auth = {}, defaultHeaders = {}) {
    this.endpoint = endpoint;
    this.auth = auth;
    this.defaultHeaders = defaultHeaders;
    this.getHeaders = getRequestHeaders(this.auth, this.defaultHeaders);

    this.axios = axios.create({
      baseURL: this.endpoint,
      timeout: 30000,
    });

    this.axios.interceptors.request.use((request) => {
      handleRequest(request);
      return request;
    });

    return this;
  },

  add(companyId, subcompanyId, asset) {
    const url = getAddUrl(companyId, subcompanyId);
    return this.axios
      .post(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  get(companyId, subcompanyId, filters, limit, page, orderBy, order) {
    const url = getAssetsUrl(companyId, subcompanyId);
    const params = { ...filters, limit, page, orderBy, order };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getById(assetId, companyId, subcompanyId) {
    const url = getByIdAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .get(url, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * @author Jorge Casas
   *
   * @creationDate 23/07/2024
   *
   * @function getQRCode
   *
   * @description This function is used to get the QR code of an asset.
   *
   * @param {string} assetId - The id of the asset.
   *
   * @returns {Promise}
   *
   * @version 3.4.14
   *
   */
  getQRCode(assetId) {
    const url = getQRCodeUrl(assetId);
    return this.axios
      .get(url, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * @author Jorge Casas
   * 
   * @creationDate 23/07/2024
   * 
   * @function putRegenerateCodes
   * 
   * @description This function is used to regenerate the codes of an asset. (qrs and barcode codes of the asset)
   * 
   * @param {string} companyId - The id of
   * @param {string} subcompanyId - The id of the subcompany.
   * @param {string} assetId - The id of the asset.
   * 
   * @returns {Promise}
   * 
   * @version 3.4.14
   */
  putRegenerateCodes(companyId, subcompanyId, assetId) {
    const url = regenerateCodesUrl(companyId, subcompanyId, assetId);
    return this.axios
      .put(url, {}, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * @author Jorge Casas
   *
   * @function getCodesLabelsByAssetPDF
   *
   * @description This function is used to get the codes labels by asset in PDF format.
   *
   * @param {string} companyId - The id of
   * @param {string} subcompanyId - The id of the subcompany.
   * @param {Object} filters - The filters to apply to the report.
   *
   */
  getCodesLabelsByAssetPDF(assetId, companyId, subcompanyId, filters) {
    const url = getCodesLabelsByAssetPDFUrl(assetId, companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  update(assetId, companyId, subcompanyId, asset) {
    const url = updateAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  sendTokenAsign(assetId, companyId, subcompanyId, asset) {
    const url = sendTokenAsignAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .post(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  disable(assetId, companyId, subcompanyId, asset) {
    const url = disableAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  enable(assetId, companyId, subcompanyId) {
    const url = enableAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, {}, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getFormConfig(companyId, subcompanyId) {
    const url = getFormConfigUrl(companyId, subcompanyId);
    return this.axios
      .get(url, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getDisableFormConfig(companyId, subcompanyId) {
    const url = getDisableFormConfigUrl(companyId, subcompanyId);
    return this.axios
      .get(url, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getHistorics(assetId, companyId, subcompanyId, filters, limit, page, orderBy, order) {
    const url = getHistoricsUrl(assetId, companyId, subcompanyId);
    const params = { ...filters, limit, page, orderBy, order };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  acceptHistoric(hId, assetId, companyId, subcompanyId) {
    const url = acceptHistoricUrl(hId, assetId, companyId, subcompanyId);
    return this.axios
      .put(url, {}, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  rejectHistoric(hId, assetId, companyId, subcompanyId) {
    const url = rejectHistoricUrl(hId, assetId, companyId, subcompanyId);
    return this.axios
      .put(url, {}, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  requestUpdate(assetId, companyId, subcompanyId, asset) {
    const url = requestUpdateAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },
  outputAsset(assetId, companyId, subcompanyId, asset) {
    const url = outputAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },
  inputAsset(assetId, companyId, subcompanyId, asset) {
    const url = inputAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },
  aceptOutput(assetId, companyId, subcompanyId, asset) {
    const url = aceptOutputAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },
  rejectOutput(assetId, companyId, subcompanyId, asset) {
    const url = rejectOutputAssetUrl(assetId, companyId, subcompanyId);
    return this.axios
      .put(url, asset, { ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },
  /**
   * Saves a signed custody voucher (AUTOSUR) in the asset's files, locked. The
   * back puts the date and the user. Content-Type: axios drops the JSON one of
   * getHeaders() for FormData and the browser sets the multipart boundary.
   */
  addCustodyVoucher(assetId, companyId, subcompanyId, blob, fileName) {
    const url = custodyVouchersUrl(assetId, companyId, subcompanyId);
    const data = new FormData();
    data.append('file', blob, fileName);
    return this.axios
      .post(url, data, { ...this.getHeaders(), timeout: 60000 })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * The back adds the same PDF, locked, to every asset or to none; assetIds must
   * be ALL the employee's active assets in the subcompany (409 otherwise).
   */
  addEmployeeCustodyVoucher(companyId, subcompanyId, employeeId, assetIds, blob, fileName) {
    const url = employeeCustodyVouchersUrl(companyId, subcompanyId);
    const data = new FormData();
    data.append('employeeId', employeeId);
    data.append('assetIds', JSON.stringify(assetIds));
    data.append('file', blob, fileName);
    return this.axios
      .post(url, data, { ...this.getHeaders(), timeout: 60000 })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  putBill(assetId, companyId, subcompanyId, bill, files) {
    const url = editBillUrl(assetId, companyId, subcompanyId);

    var data = new FormData();

    for (const file of files) {
      data.append('file', file);
    }
    data.append('billNumber', bill.billNumber);
    data.append('billFolio', bill.billFolio);
    data.append('billDate', bill.billDate);
    data.append('billConcept', bill.billConcept);
    data.append('billImport', bill.billImport);
    data.append('finish', {});

    if (files.length > 0) {
      return this.axios
        .post(url, data, { ...this.getHeaders() })
        .then(handleApiSucess)
        .catch(handleApiError);
    } else {
      return this.axios
        .post(url, { bill: bill }, { ...this.getHeaders() })
        .then(handleApiSucess)
        .catch(handleApiError);
    }
  },

  /**
   * @putCustomsDocuments
   * @author Jorge Casas
   * @creationDate 22/04/2024
   *
   * This function is used to put customs documents to an asset in the database.
   *
   * @param {string} assetId - The id of the asset.
   * @param {string} companyId - The id of
   * @param {string} subcompanyId - The id of the subcompany.
   * @param {Object} customsDocuments - The customs documents to be put.
   * @param {Array} files - The files to be uploaded.
   *
   */
  putCustomsDocuments(assetId, companyId, subcompanyId, customsDocuments, files) {
    try {
      const url = editCustomsDocumentsUrl(assetId, companyId, subcompanyId);

      var data = new FormData();

      for (const file of files) {
        data.append('file', file);
      }
      data.append(
        'customsDocumentsPedimentoNumber',
        customsDocuments.customsDocumentsPedimentoNumber
      );
      data.append('customsDocumentsCustomsKey', customsDocuments.customsDocumentsCustomsKey);
      data.append('customsDocumentsDate', customsDocuments.customsDocumentsDate);
      data.append('customsDocumentsPedimentoKey', customsDocuments.customsDocumentsPedimentoKey);
      data.append('customsDocumentsStatus', customsDocuments.customsDocumentsStatus);
      data.append('customsDocumentsImport', customsDocuments.customsDocumentsImport);
      data.append('customsDocumentsQuantity', customsDocuments.customsDocumentsQuantity);
      data.append('finish', {});

      if (files.length > 0) {
        return this.axios
          .post(url, data, { ...this.getHeaders() })
          .then(handleApiSucess)
          .catch(handleApiError);
      } else {
        return this.axios
          .post(url, { customsDocuments: customsDocuments }, { ...this.getHeaders() })
          .then(handleApiSucess)
          .catch(handleApiError);
      }
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  downloadFileToConverBase64(urlFile) {
    const url = downloadFileToConverBase64Url();

    return this.axios
      .post(url,  JSON.stringify({ urlFile: urlFile }), { ...this.getHeaders() })
      .then(handleApiSucess)
      .then((response) => response.data)
      .catch(handleApiError);
  },
};

export default service;



// WEBPACK FOOTER //
// src/lib/api/assets.api.js